import type { ReactElement } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AnswerSource, SurveyQuestionType } from 'src/redux/survey/survey.types';
import { freeformListForFilter } from 'src/pages/view-survey/view-survey.helpers';
import type { CombinedQuestionStats } from 'src/pages/view-survey/view-survey.types';
import { SurveyGroupCompareAnswerAxisTick } from 'src/pages/survey-results/components/survey-group-compare-answer-axis-tick';
import { PortalDsCard, portalDs } from 'src/components/portal-design-system';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';
import { cn } from 'src/lib/utils';
import PaginatedFreeformList from './paginated-freeform-list';
import QuestionFeedbackRegion from './question-feedback-region';
import { emptyFreeformMessage } from '../../services/source-filter.helpers';
import {
  buildQuestionBreakout,
  type BreakoutModel,
  type BreakoutRow,
  type RespondentScope,
} from '../../services/demographic-breakout.helpers';
import QuestionSummary from './question-summary';

const OVERVIEW_CHART_COLORS = [
  '#2563EB',
  '#059669',
  '#F59E0B',
  '#EF4444',
  '#6366F1',
  '#EC4899',
  '#0891B2',
  '#D97706',
  '#8B5CF6',
];
const OVERVIEW_CHART_GRID = '#EDEEF2';
const OVERVIEW_CHART_TICK = '#5E6278';

interface QuestionCardProps {
  index: number;
  question: CombinedQuestionStats;
  breakoutAttribute: string | null;
  respondentScope: RespondentScope;
  sourceFilter: AnswerSource;
  surveyBreakoutSegments?: Array<{ label: string; n: number }>;
  isSummaryGenerating?: boolean;
  summaryError?: boolean;
  canRegenerateSummary?: boolean;
  onRegenerateSummary?: () => void;
}

function questionTypeLabel(questionType?: SurveyQuestionType): string {
  switch (questionType) {
    case SurveyQuestionType.MultiSelect:
      return 'Multi-select';
    case SurveyQuestionType.Freeform:
      return 'Freeform';
    default:
      return 'Single-select';
  }
}

function BreakoutTooltip({
  active,
  payload,
  label,
  segments,
}: {
  active?: boolean;
  payload?: Array<{
    dataKey?: string | number;
    name?: string;
    value?: number;
    color?: string;
    payload?: BreakoutRow;
  }>;
  label?: string;
  segments: BreakoutModel['segments'];
}): ReactElement | null {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="rounded-lg border border-[#EDEEF2] bg-white px-3 py-2.5 shadow-[0_4px_16px_rgba(13,15,26,0.10)]">
      <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.4px] text-[#8A8EA0]">
        {label}
      </p>
      <ul className="space-y-1">
        {payload.map((entry) => {
          const segment = segments.find((item) => item.key === entry.dataKey);
          const count = entry.payload?.[`${entry.dataKey}__n`];
          return (
            <li
              key={String(entry.dataKey)}
              className="flex items-center justify-between gap-4"
            >
              <span className="flex min-w-0 items-center gap-1.5 text-[12px] font-semibold text-[#5E6278]">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: entry.color }}
                />
                <span className="truncate">
                  {segment?.label ?? entry.name}
                </span>
              </span>
              <span className="shrink-0 text-[12px] font-bold tabular-nums text-[#0D0F1A]">
                {typeof entry.value === 'number' ? `${entry.value.toFixed(1)}%` : entry.value}
                {typeof count === 'number' ? ` (n=${count})` : ''}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AggregateAnswerRow({
  answerLabel,
  percentage,
  count,
  imageUrl,
}: {
  answerLabel: string;
  percentage: number;
  count: number;
  imageUrl?: string;
}): ReactElement {
  return (
    <div className="flex items-center gap-3">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="size-10 shrink-0 rounded-md border border-[#EDEEF2] bg-[#F7F8FA] object-cover"
        />
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center justify-between gap-2 text-[13.5px]">
          <span className="truncate text-[#0D0F1A]">
            {answerLabel}
          </span>
          <span className="shrink-0 font-mono text-[13px] text-[#5E6278]">
            {percentage.toFixed(1)}
            % (n=
            {count}
            )
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-[#EDEEF2]">
          <div
            className="h-full rounded-full bg-[#2563EB]"
            style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function QuestionCard({
  index,
  question,
  breakoutAttribute,
  respondentScope,
  sourceFilter,
  surveyBreakoutSegments,
  isSummaryGenerating = false,
  summaryError = false,
  canRegenerateSummary = false,
  onRegenerateSummary,
}: QuestionCardProps): ReactElement {
  const isFreeform = question.questionType === SurveyQuestionType.Freeform;
  const isMultiSelect = question.questionType === SurveyQuestionType.MultiSelect;
  const breakout = breakoutAttribute
    ? buildQuestionBreakout(question, breakoutAttribute, respondentScope, surveyBreakoutSegments)
    : null;
  const activeSegments = breakout?.segments.filter((segment) => !segment.suppressed) ?? [];
  const suppressedSegments = breakout?.segments.filter((segment) => segment.suppressed) ?? [];
  const hasAnswerImages = question.answers.some((answer) => Boolean(answer.imageUrl));
  const sortedAnswers = [...question.answers].sort((a, b) => b.totalPercentage - a.totalPercentage);
  const freeformAnswers = question.freeformAnswers
    ? freeformListForFilter(question.freeformAnswers, sourceFilter)
    : [];

  return (
    <PortalDsCard
      id={`overview-question-${index}`}
      className="scroll-mt-4"
    >
      <div className="mb-5 flex items-start gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#EFF6FF] text-[13px] font-bold text-[#2563EB]">
          {index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className={portalDs.headingH3}>
            {question.title}
          </h2>
          <p className={cn(portalDs.caption, 'mt-1')}>
            {questionTypeLabel(question.questionType)}
          </p>
        </div>
      </div>

      <QuestionSummary
        question={question}
        isGenerating={isSummaryGenerating}
        hasError={summaryError}
        canRegenerate={canRegenerateSummary}
        onRegenerate={onRegenerateSummary}
      />

      {isFreeform ? (
        <div className="space-y-3">
          {breakoutAttribute ? (
            <p className={portalDs.bodySm}>
              Demographic breakouts are not available for freeform questions.
            </p>
          ) : null}
          <PaginatedFreeformList
            responses={freeformAnswers}
            emptyMessage={emptyFreeformMessage(sourceFilter)}
          />
        </div>
      ) : breakoutAttribute && breakout ? (
        <div className="space-y-3">
          {breakout.skipReason === 'no-attribute-data' ? (
            <p className={portalDs.bodySm}>
              No demographic data is available for this breakout on this question.
            </p>
          ) : breakout.hasData ? (
            <>
              {isMultiSelect ? (
                <p className={portalDs.bodySm}>
                  Multi-select — respondents may choose more than one answer, so shares can exceed 100%.
                </p>
              ) : null}
              <div className="h-[min(420px,70vh)] w-full min-h-[280px]">
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >
                  <BarChart
                    data={breakout.rows}
                    margin={{
                      top: 8,
                      right: 12,
                      left: 4,
                      bottom: hasAnswerImages ? 108 : 64,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={OVERVIEW_CHART_GRID}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="answerLabel"
                      interval={0}
                      tick={
                        hasAnswerImages
                          ? (props) => (
                            <SurveyGroupCompareAnswerAxisTick
                              {...props}
                              rows={breakout.rows}
                            />
                          )
                          : { fontSize: 11, fill: OVERVIEW_CHART_TICK }
                      }
                      angle={hasAnswerImages ? 0 : -28}
                      textAnchor={hasAnswerImages ? 'middle' : 'end'}
                      height={hasAnswerImages ? 100 : 72}
                      axisLine={{ stroke: OVERVIEW_CHART_GRID }}
                      tickLine={false}
                    />
                    <YAxis
                      domain={isMultiSelect ? ['auto', 'auto'] : [0, 100]}
                      tick={{ fontSize: 11, fill: OVERVIEW_CHART_TICK }}
                      tickFormatter={(value) => `${value}%`}
                      width={44}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RechartsTooltip
                      content={(props) => (
                        <BreakoutTooltip
                          active={props.active}
                          payload={props.payload as Array<{
                            dataKey?: string | number;
                            name?: string;
                            value?: number;
                            color?: string;
                            payload?: BreakoutRow;
                          }> | undefined}
                          label={typeof props.label === 'string' ? props.label : undefined}
                          segments={activeSegments}
                        />
                      )}
                    />
                    <Legend wrapperStyle={{ paddingTop: 12, fontSize: 12, color: OVERVIEW_CHART_TICK }} />
                    {activeSegments.map((segment, segmentIndex) => (
                      <Bar
                        key={segment.key}
                        dataKey={segment.key}
                        name={segment.label}
                        fill={OVERVIEW_CHART_COLORS[segmentIndex % OVERVIEW_CHART_COLORS.length]}
                        radius={[4, 4, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {suppressedSegments.length > 0 ? (
                <p className={portalDs.caption}>
                  Low sample:
                  {' '}
                  {suppressedSegments
                    .map((segment) => `${segment.label} (n=${segment.n})`)
                    .join(', ')}
                </p>
              ) : null}
            </>
          ) : (
            <p className={portalDs.bodySm}>
              Not enough sample to show this breakout.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {sortedAnswers.map((answer) => (
            <AggregateAnswerRow
              key={answer.id}
              answerLabel={answer.displayName || answer.title}
              percentage={answer.totalPercentage}
              count={answer.totalUsers}
              imageUrl={answer.imageUrl}
            />
          ))}
          {sortedAnswers.length === 0 ? (
            <p className={portalDs.bodySm}>
              No answers recorded yet.
            </p>
          ) : null}
        </div>
      )}

      {question.hasFeedback ? (
        <QuestionFeedbackRegion
          answers={question.answers}
          sourceFilter={sourceFilter}
        />
      ) : null}

      {question.ai_icc != null ? (
        <div className="mt-4 border-t border-[#EDEEF2] pt-3">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={cn(portalDs.caption, 'cursor-help underline decoration-dotted underline-offset-2')}>
                  AI sample quality:
                  {' '}
                  raw N
                  {' '}
                  {question.ai_raw_n ?? '—'}
                  , effective N
                  {' '}
                  {question.ai_effective_n != null ? Math.round(question.ai_effective_n) : '—'}
                  , ICC
                  {' '}
                  {Number(question.ai_icc).toFixed(2)}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                AI personas produce three correlated responses each, so effective N is lower than raw N.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ) : null}
    </PortalDsCard>
  );
}

export default QuestionCard;
